import React from 'react';
import PropTypes from 'prop-types';
import { TableBody, TableCell, TableRow } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';

const _FEATURES = [
  {
    category: 'WHAT IT IS',
    features: [
      {
        name: 'Legally compliant pay gap reports',
        compliance: true,
        modeling: true,
      },
      {
        name: 'Global jurisdictional reports',
        compliance: true,
        modeling: true,
      },
      {
        name: 'Company-wide global pay equity analysis',
        compliance: false,
        modeling: true,
      },
    ],
  },
  {
    category: 'DETAILS',
    features: [
      {
        name: 'Support for 43 jurisdictions',
        compliance: true,
        modeling: true,
      },
      {
        name: 'Save HR and Legal team 8-12 weeks per report',
        compliance: true,
        modeling: true,
      },
      {
        name: 'Support for EU Pay Transparency Directive reporting',
        compliance: true,
        modeling: true,
      },
      {
        name: 'Support for EU CSRD ESRS S1 and S2 reporting',
        compliance: true,
        modeling: true,
      },
      {
        name: 'No manual data cleaning necessary',
        compliance: true,
        modeling: true,
      },
      {
        name: 'Reports ready in 1 week from data selection and cleaning',
        compliance: true,
        modeling: true,
      },
      {
        name: 'Ensure pay is compliant and consistent',
        compliance: false,
        modeling: true,
      },
      {
        name: 'Analyse, explain, and remediate gaps',
        compliance: false,
        modeling: true,
      },
      {
        name: 'Rightsized approach: different models for different goals',
        compliance: false,
        modeling: true,
      },
      {
        name: 'Pay recommendations with intelligent cost-effective budgeting',
        compliance: false,
        modeling: true,
      },
      {
        name: 'Rich visualizations, root cause analysis, balanced budgeting approach',
        compliance: false,
        modeling: true,
      },
      {
        name: 'Great for Board of Directors request, IPO events, or M&A',
        compliance: false,
        modeling: true,
      },
    ],
  },
  {
    category: 'ADD-ONS',
    features: [
      {
        name: 'AI insights on pay gap drivers',
        compliance: true,
        modeling: true,
      },
      { name: 'Rich visualizations', compliance: true, modeling: true },
      { name: 'Executive summary PDFs', compliance: true, modeling: true },
      {
        name: 'Support for internal and external comms',
        compliance: true,
        modeling: true,
      },
      {
        name: 'Rush turnaround available on request',
        compliance: true,
        modeling: true,
      },
      { name: 'Historical trends analysis', compliance: true, modeling: true },
      { name: 'Competitive analysis', compliance: true, modeling: true },
      {
        name: 'Deep dive add-on with AI root cause analysis',
        compliance: false,
        modeling: true,
      },
    ],
  },
];

const FeaturesTable = ({ features = _FEATURES }) => {
  return (
    <TableBody>
      {features.map((category) => (
        <React.Fragment key={category.category}>
          <TableRow sx={{ bgcolor: '#f8fafc' }}>
            <TableCell
              colSpan={3}
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.7rem', md: '0.9rem' },
                color: '#1e293b',
                py: 2,
                pr: { xs: 0, md: 2 },
                pl: { xs: 1, md: 2 },
              }}
            >
              {category.category}
            </TableCell>
          </TableRow>
          {category.features.map((feature, index) => (
            <TableRow
              key={index}
              sx={{
                bgcolor: 'white',
                '&:hover': { bgcolor: '#f5f7fd' },
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 500,
                  color: '#374151',
                  py: 2,
                  fontSize: { xs: '0.8rem', md: '1rem' },
                  pr: { xs: 0, md: 2 },
                  pl: { xs: 1, md: 2 },
                }}
              >
                {feature.name}
              </TableCell>
              <TableCell sx={{ textAlign: 'center', py: 2, px: { xs: 0, md: 2 } }}>
                {feature.compliance ? (
                  <CheckCircle sx={{ fontSize: 24, color: '#47e5c0' }} />
                ) : (
                  <CancelIcon sx={{ fontSize: 24, color: '#e3e8ef' }} />
                )}
              </TableCell>
              <TableCell sx={{ textAlign: 'center', py: 2, px: { xs: 0, md: 2 } }}>
                {feature.modeling ? (
                  <CheckCircle sx={{ fontSize: 24, color: '#47e5c0' }} />
                ) : (
                  <CancelIcon sx={{ fontSize: 24, color: '#eef2ff' }} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </React.Fragment>
      ))}
    </TableBody>
  );
};

FeaturesTable.propTypes = {
  features: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string.isRequired,
      features: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          compliance: PropTypes.bool.isRequired,
          modeling: PropTypes.bool.isRequired,
        }),
      ).isRequired,
    }),
  ),
};

export default FeaturesTable;
